/* eslint-disable no-console, class-methods-use-this */
// Cliente SIP WebRTC para Chatwoot
// Auto-registro con Voice Inbox configurado

import JsSIP from 'jssip';

class VoiceSIPClient {
  constructor() {
    this.ua = null;
    this.currentSession = null;
    this.isRegistered = false;
    this.config = null;
    this.userInfo = {};
    this.lastHangupSentAt = null;
  }

  sipEventDetail(event = {}) {
    const message = event.message || event.response || {};
    return {
      cause: event.cause,
      originator: event.originator,
      status: message.status_code,
      reason: message.reason_phrase,
    };
  }

  // Inicializar con configuracion del Voice Inbox
  async initialize(voiceConfig, userInfo = {}) {
    if (!voiceConfig || voiceConfig.provider !== 'sip_generic') {
      return false;
    }

    // Parar UA anterior si existe
    if (this.ua) {
      try {
        this.ua.stop();
      } catch {
        /* ua stop failed */
      }
      this.ua = null;
    }

    this.config = voiceConfig.provider_config;
    this.userInfo = userInfo;

    try {
      // Configurar JsSIP
      const socket = new JsSIP.WebSocketInterface(this.config.websocket_server);

      const configuration = {
        sockets: [socket],
        uri: this.config.sip_uri,
        password: this.config.password,
        display_name: this.config.display_name || 'Chatwoot Agent',
        session_timers: false,
        register: true,
        register_expires: 600,
      };

      this.ua = new JsSIP.UA(configuration);

      // Event listeners
      this.setupEventListeners();

      // Iniciar UA
      this.ua.start();

      return true;
    } catch (error) {
      console.error('[Voice SIP] Init failed', error);
    }
    return false;
  }

  setupEventListeners() {
    // Conexion WebSocket
    this.ua.on('connected', () => {
      this.isRegistered = false;
      console.info('[Voice SIP] WebSocket connected');
    });

    this.ua.on('disconnected', () => {
      this.isRegistered = false;
      console.warn('[Voice SIP] WebSocket disconnected');
    });

    // Registro SIP
    this.ua.on('registered', () => {
      this.isRegistered = true;
      console.info('[Voice SIP] Registered', { uri: this.config.sip_uri });
      window.dispatchEvent(
        new CustomEvent('sip:registered', {
          detail: { uri: this.config.sip_uri, userInfo: this.userInfo },
        })
      );
    });

    this.ua.on('unregistered', () => {
      this.isRegistered = false;
      console.warn('[Voice SIP] Unregistered');
      window.dispatchEvent(
        new CustomEvent('sip:unregistered', {
          detail: { userInfo: this.userInfo },
        })
      );
    });

    this.ua.on('registrationFailed', e => {
      this.isRegistered = false;
      console.error('[Voice SIP] Registration failed', this.sipEventDetail(e));

      // Emitir evento de error
      window.dispatchEvent(
        new CustomEvent('sip:registrationFailed', {
          detail: {
            cause: e.cause,
            status: e.response?.status_code,
            reason: e.response?.reason_phrase,
          },
        })
      );
    });

    // Llamadas entrantes
    this.ua.on('newRTCSession', e => {
      const session = e.session;

      if (session.direction === 'incoming') {
        this.handleIncomingCall(session);
      }
    });
  }

  // Realizar llamada
  makeCall(phoneNumber) {
    if (!this.isRegistered) {
      console.warn('[Voice SIP] Cannot make call: UA is not registered', {
        target: phoneNumber,
        uri: this.config?.sip_uri,
      });
      return null;
    }

    const eventHandlers = {
      progress: e => {
        console.info('[Voice SIP] Outgoing call progress', {
          target: phoneNumber,
          ...this.sipEventDetail(e),
        });
      },
      failed: e => {
        console.error('[Voice SIP] Outgoing call failed', {
          target: phoneNumber,
          ...this.sipEventDetail(e),
        });
        this.currentSession = null;
      },
      ended: e => {
        console.info('[Voice SIP] Outgoing call ended', {
          target: phoneNumber,
          ...this.sipEventDetail(e),
        });
        this.currentSession = null;
      },
      confirmed: e => {
        console.info('[Voice SIP] Outgoing call confirmed', {
          target: phoneNumber,
          ...this.sipEventDetail(e),
        });
      },
      accepted: e => {
        console.info('[Voice SIP] Outgoing call accepted', {
          target: phoneNumber,
          ...this.sipEventDetail(e),
        });
      },
    };

    const options = {
      eventHandlers,
      mediaConstraints: { audio: true, video: false },
      pcConfig: {
        iceServers: [{ urls: ['stun:stun.l.google.com:19302'] }],
      },
    };

    try {
      this.currentSession = this.ua.call(phoneNumber, options);
      return this.currentSession;
    } catch (error) {
      console.error('[Voice SIP] Call start failed', {
        target: phoneNumber,
        error,
      });
    }
    return null;
  }

  getTrackedSessions() {
    const sessions = [];

    if (this.currentSession) {
      sessions.push(this.currentSession);
    }

    const sessionsKey = ['_', 'sessions'].join('');
    const uaSessions = this.ua?.[sessionsKey];
    if (uaSessions && typeof uaSessions === 'object') {
      Object.values(uaSessions).forEach(session => {
        if (session && !sessions.includes(session)) {
          sessions.push(session);
        }
      });
    }

    return sessions.filter(session => {
      try {
        return !session.isEnded?.();
      } catch {
        return true;
      }
    });
  }

  terminateSession(session) {
    if (!session) return false;

    const isEstablished = session.isEstablished?.() === true;
    const isInProgress = session.isInProgress?.() === true;

    try {
      if (isEstablished) {
        // Established dialogs must be ended with a plain BYE.
        session.terminate();
      } else if (isInProgress) {
        // Early dialogs need CANCEL/reject semantics, not BYE.
        session.terminate({
          status_code: 487,
          reason_phrase: 'Request Terminated',
        });
      } else {
        session.terminate();
      }

      window.dispatchEvent(
        new CustomEvent('sip:hangupSent', {
          detail: {
            established: isEstablished,
            inProgress: isInProgress,
            remote: session.remote_identity?.uri?.toString?.(),
          },
        })
      );

      this.lastHangupSentAt = Date.now();
      return true;
    } catch {
      try {
        const requestKey = '_request';
        session[requestKey]?.cancel?.();
      } catch {
        /* sip cancel failed */
      }
    }

    return false;
  }

  // Colgar/cancelar llamada actual, incluso antes de que conteste.
  hangup(session = this.currentSession) {
    const trackedSessions = this.getTrackedSessions();
    const sessions = session
      ? [
          session,
          ...trackedSessions.filter(activeSession => activeSession !== session),
        ]
      : trackedSessions;

    if (!sessions.length) return false;

    let terminated = false;
    sessions.forEach(activeSession => {
      terminated = this.terminateSession(activeSession) || terminated;

      if (this.currentSession === activeSession) {
        this.currentSession = null;
      }
    });

    if (!this.getTrackedSessions().length) {
      this.currentSession = null;
    }

    return terminated;
  }

  handleIncomingCall(session) {
    this.currentSession = session;
    session.on('ended', () => {
      if (this.currentSession === session) this.currentSession = null;
      window.dispatchEvent(new CustomEvent('voice:incomingCallEnded'));
    });
    session.on('failed', () => {
      if (this.currentSession === session) this.currentSession = null;
      window.dispatchEvent(new CustomEvent('voice:incomingCallEnded'));
    });

    // Emitir evento global para que los modales lo capturen
    window.dispatchEvent(
      new CustomEvent('voice:incomingCall', {
        detail: {
          session,
          from: session.remote_identity.uri.user,
        },
      })
    );
  }

  // Desconectar
  disconnect() {
    this.hangup();

    if (this.ua) {
      this.ua.stop();
      this.isRegistered = false;
    }
  }

  // Estado
  getStatus() {
    return {
      isRegistered: this.isRegistered,
      hasActiveCall: this.currentSession !== null,
      config: this.config,
    };
  }
}

// Singleton
const voiceSIPClient = new VoiceSIPClient();

export default voiceSIPClient;
